// PeriodSelector — Year + Month + Week + Day chooser.
// Day pills are calendar-like: large square cells with the date as the hero
// and the weekday name underneath. Selected day gets the accent fill.

import React from 'react';
import { Segmented, Select } from 'antd';
import { MONTHS, SHORT_MONTHS, DAYS, SHORT_DAYS, weeksInMonth, dateForCell, formatDmy } from './manualReportUtils';
import { tokens } from './theme';

const fieldLabel = (text) => (
  <div style={{
    ...tokens.type.label,
    color: tokens.color.textFaint,
    marginBottom: tokens.space.xs,
  }}>{text}</div>
);

// Compact day cell — weekday | date number | mon/yy on one tight column.
// "Today" is signalled by a tiny accent dot in the top-right corner — no
// extra row so cells stay short and uniform.
const DayCell = ({ year, monthIndex, week, dayLabel, shortLabel, selected, onClick }) => {
  const date = dateForCell(year, monthIndex, week, dayLabel);
  const inMonth = date && date.getUTCMonth() === monthIndex;
  const isToday = (() => {
    if (!date) return false;
    const t = new Date();
    return t.getFullYear() === date.getUTCFullYear()
      && t.getMonth() === date.getUTCMonth()
      && t.getDate() === date.getUTCDate();
  })();

  const dayNum = date ? String(date.getUTCDate()).padStart(2, '0') : '—';
  const monStr = date ? String(date.getUTCMonth() + 1).padStart(2, '0') : '';

  return (
    <button
      type="button"
      onClick={inMonth ? onClick : undefined}
      disabled={!inMonth}
      style={{
        position: 'relative',
        flex: '1 1 0',
        minWidth: 64,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: '6px 4px',
        border: `1px solid ${selected && inMonth ? tokens.color.accent : tokens.color.border}`,
        borderRadius: tokens.radius.sm,
        background: !inMonth
          ? tokens.color.surfaceAlt
          : selected
            ? tokens.color.accentBg
            : tokens.color.surface,
        cursor: inMonth ? 'pointer' : 'not-allowed',
        transition: `all ${tokens.duration.fast} ease`,
        opacity: !inMonth ? 0.4 : 1,
      }}
    >
      {isToday && inMonth && (
        <span style={{
          position: 'absolute',
          top: 5, right: 6,
          width: 6, height: 6,
          borderRadius: '50%',
          background: '#f97316',
          boxShadow: '0 0 0 2px #fff7ed',
        }} title="Today" />
      )}
      <div style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.7,
        textTransform: 'uppercase',
        color: selected && inMonth ? tokens.color.accent : tokens.color.textFaint,
        lineHeight: 1,
      }}>
        {shortLabel}
      </div>
      <div style={{
        fontSize: 17,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: !inMonth ? tokens.color.textFaint : selected ? tokens.color.accent : tokens.color.textStrong,
        lineHeight: 1.15,
        letterSpacing: -0.4,
      }}>
        {dayNum}
      </div>
      <div style={{
        ...tokens.type.mono,
        fontSize: 9,
        color: !inMonth ? tokens.color.textFaint : selected ? tokens.color.accent : tokens.color.textMuted,
        lineHeight: 1.1,
      }}>
        {inMonth ? `${monStr}/${String(date.getUTCFullYear()).slice(2)}` : '—'}
      </div>
    </button>
  );
};

export default function PeriodSelector({ year, monthIndex, week, day, onChange }) {
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];
  const numWeeks = weeksInMonth(year, monthIndex);
  const weeks = Array.from({ length: numWeeks }, (_, i) => i + 1);
  const set = (patch) => onChange({ year, monthIndex, week, day, ...patch });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.lg }}>
      {/* Year + Month + Week — one tidy row */}
      <div style={{ display: 'flex', gap: tokens.space.lg, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 100 }}>
          {fieldLabel('Year')}
          <Select
            value={year}
            onChange={(y) => set({ year: y })}
            options={yearOptions.map(y => ({ value: y, label: String(y) }))}
            style={{ width: 110 }}
          />
        </div>
        <div style={{ flex: '2 1 380px', minWidth: 320 }}>
          {fieldLabel('Month')}
          <Segmented
            value={SHORT_MONTHS[monthIndex]}
            onChange={(s) => set({ monthIndex: SHORT_MONTHS.indexOf(s), week: 1 })}
            options={SHORT_MONTHS}
            block
          />
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
          {fieldLabel('Week')}
          <Segmented
            value={week}
            onChange={(w) => set({ week: w })}
            options={weeks.map(w => ({ value: w, label: `Week ${w}` }))}
            block
          />
        </div>
      </div>

      {/* Day — calendar-like cells */}
      <div>
        {fieldLabel('Day')}
        <div style={{
          display: 'flex',
          gap: tokens.space.xs,
        }}>
          {DAYS.map((d, i) => (
            <DayCell
              key={d}
              year={year}
              monthIndex={monthIndex}
              week={week}
              dayLabel={d}
              shortLabel={SHORT_DAYS[i]}
              selected={d === day}
              onClick={() => set({ day: d })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
